import { useTranslation } from "react-i18next";
import { useState } from "react";
import Api from "../../../../Api";
import TeambookTextForm from "../../../default_components/TeambookTextForm";
import GetAllMilestones from "../../requests/GetAllMilestones";
import "moment/locale/de";
import "moment/locale/fr";
import lsKeys from "../../../default_values/defaultKeys";
import { DateTime } from "luxon";
import TeambookDatePicker from "../../../default_components/teambook_date_selector/DatePicker";
import TeambookDialog from "../../../default_components/TeambookDialog";
import PropTypes from "prop-types";

const MilestoneEditing = ({ milestone, setEditingMilestone, setMilestones }) => {
  const { t } = useTranslation();

  const canEditMilestone = !["regular", "self_planner", "contractor"].includes(localStorage.getItem(lsKeys.ROLE));
  const [milestoneName, setMilestoneName] = useState(milestone.name);
  const [milestoneStartDate, setMilestoneStartDate] = useState(DateTime.fromISO(milestone.start_date));
  const [milestoneEndDate, setMilestoneEndDate] = useState(DateTime.fromISO(milestone.end_date));
  const [errorMessage, setErrorMessage] = useState(null);
  const [errorField, setErrorField] = useState(null);
  const isCorrectDates = milestoneStartDate <= milestoneEndDate;

  const closeErrorMessage = () => {
    setErrorMessage(null);
    setErrorField(null);
  };

  const updateMilestone = () => {
    if (isCorrectDates) {
      setErrorMessage(null);
      setErrorField(null);
      if (milestoneName.length < 3 || milestoneName.length > 30) {
        setErrorMessage("Milestone name must be 3 to 30 characters long.");
        setErrorField("name");
      } else {
        Api.Milestones.update({
          id: milestone.id,
          name: milestoneName,
          start_date: milestoneStartDate,
          end_date: milestoneEndDate,
        })
          .then(() => {
            GetAllMilestones(setMilestones);
            setEditingMilestone(null);
          })
          .catch((error) => {
            setErrorMessage(error.response.data.error);
            setErrorField(null);
          });
      }
    }
  };

  const deleteMilestone = () => {
    Api.Milestones.delete(milestone.id).then(() => {
      GetAllMilestones(setMilestones);
      setEditingMilestone(null);
    });
  };

  return (
    <TeambookDialog
      open
      onClose={() => {
        setEditingMilestone(null);
        setErrorMessage(null);
        setErrorField(null);
      }}
      title={t("planning.milestones.edit")}
      dialogClassName="milestone-dialog"
      withCloseButton
      content={
        <>
          <div className="milestone-editing__editing">
            <TeambookTextForm
              disabled={!canEditMilestone}
              id="editingMilestoneName"
              fieldName={t("planning.milestones.name")}
              onChange={(text) => setMilestoneName(text)}
              type={"text"}
              fieldValue={milestoneName}
              width={"78%"}
              placeholder={"Milestone #1"}
              errorField={errorField}
              errorMessage={errorMessage}
              closeErrorMessage={closeErrorMessage}
              field={"name"}
            />
          </div>

          <div className="milestone-editing__info">
            <div className="milestone-editing__textfield">
              <div className="milestone-date-picker__container">
                <p>{t("projects.start_date")}</p>
                <TeambookDatePicker
                  onDayClick={(date) => setMilestoneStartDate(date)}
                  from={milestoneStartDate}
                  range={1}
                  type="default"
                  variant="inline"
                  formatStart={"d MMM, yyyy"}
                  width={86}
                  height={24}
                />
              </div>

              <div className="milestone-date-picker__container">
                <p>{t("projects.end_date")}</p>
                <TeambookDatePicker
                  onDayClick={(date) => setMilestoneEndDate(date)}
                  from={milestoneEndDate}
                  range={1}
                  type="default"
                  variant="inline"
                  formatStart={"d MMM, yyyy"}
                  width={86}
                  height={24}
                />
              </div>
            </div>
          </div>
        </>
      }
      actionButtons={
        canEditMilestone
          ? [
              {
                text: t("delete"),
                onClick: deleteMilestone,
                id: "deleteMilestone",
                type: "outlined",
              },
              {
                text: t("save"),
                onClick: updateMilestone,
                id: "saveMilestone",
                disabled: !isCorrectDates,
              },
            ]
          : []
      }
    />
  );
};

MilestoneEditing.propTypes = {
  milestone: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    start_date: PropTypes.string.isRequired,
    end_date: PropTypes.string.isRequired,
  }).isRequired,
  setEditingMilestone: PropTypes.func.isRequired,
  setMilestones: PropTypes.func.isRequired,
};

export default MilestoneEditing;
